import { useRouter } from 'next/router';
import { getLocale } from '@hub/libs/constants';
import { useTranslation } from 'next-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { Link } from '@hub/common/Link/Link';
import { BearingTableDimensions } from '@hub/bearing-hub/BearingHubPage/BearingsTable/BearingTableDimensions';
import { WidenImage } from '@hub/common/Widen/WidenImage';
import { Button } from '@hub/common/Button/Button';
import type { ShapeDescriptor } from '@hub/bearing-hub/common/shapes/shapes';
import { useNextHashQueryParams } from '@hub/hooks/useNextQueryParams';
import { getRedirectLinkToProductDetails } from '@hub/bearing-hub/common/link.factory';
import type { IbhPartsResponseDto } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhShape } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import type { IbhDimensions } from '@hub/bearing-hub/common/dimensions/dimensions';
import { dimensionKey, ShapeDimensionsDefinitionForm } from '@hub/bearing-hub/common/dimensions/dimensions';
import { UnitSystem } from '@igus/kopla-domain';
import { getLengthCode } from '@hub/bearing-hub/common/useUnitSystem/useUnitSystem';
import { formatPrice } from '@hub/libs/product-price-utils';
import { createNumberFormatter } from '@icommerce/i18n';

export interface SimilarPartsTableProps {
  similarParts: IbhPartsResponseDto[];
  selectedShape: ShapeDescriptor;
  hideTitle?: boolean;
  showPrice?: boolean;
  showArticleNumber?: boolean;
  showDeliveryTime?: boolean;
  showDimensionsAsCols?: boolean;
  isLink?: boolean;
  dimensions: IbhDimensions;
  unitSystem: UnitSystem;
  onChoose?: (dimensions: IbhDimensions, similarPart: IbhPartsResponseDto) => void;
}

export const SimilarPartsTable = ({
  similarParts,
  selectedShape,
  isLink = true,
  showDimensionsAsCols = true,
  showArticleNumber = false,
  showDeliveryTime = false,
  showPrice = false,
  hideTitle = false,
  dimensions,
  onChoose,
  unitSystem,
}: SimilarPartsTableProps) => {
  const { t } = useTranslation(['common', 'category/categoryPage']);
  const router = useRouter();
  const locale = getLocale(router);
  const hashParams = useNextHashQueryParams();

  const selectedShapeKey = selectedShape?.shapeKey;
  const formDefinitions = ShapeDimensionsDefinitionForm[selectedShapeKey];

  const debouncedOnDimensionChange = useDebouncedCallback(
    (newDimensions: IbhDimensions, similarPart: IbhPartsResponseDto) => {
      onChoose?.(newDimensions, similarPart);
    },
    500
  );

  return (
    <div className="mt-6" data-testid="similar-parts-table">
      {similarParts?.length > 0 && (
        <>
          {!hideTitle && <p>{t(`COMMON.SIMILAR_PARTS.TITLE`, { ns: 'bearing-hub/bearingHub' })}</p>}
          <>
            <div className="lg:hidden flex flex-col mt-6" data-testid="similar-parts-container-table-mobile">
              {similarParts.map(similarPart => {
                const linkTarget = getRedirectLinkToProductDetails(
                  similarPart,
                  selectedShape,
                  unitSystem,
                  hashParams,
                  false,
                  locale
                );

                return (
                  <div key={similarPart.articleNumber} className="w-full border py-5 flex flex-col mb-8">
                    {showPrice && (
                      <div className="w-full flex flex-row items-center">
                        <div className="flex justify-center w-[40%] p-8">
                          {similarPart.widenShortKey ? (
                            <WidenImage
                              shortCode={similarPart.widenShortKey}
                              title={similarPart.articleNumber}
                              filename={similarPart.widenShortKey}
                              width={216}
                              height={144}
                            />
                          ) : (
                            <WidenImage
                              shortCode={selectedShape.widenShortKeyIcon}
                              title={similarPart.articleNumber}
                              filename={selectedShape.widenShortKeyIcon}
                              width={216}
                              height={144}
                            />
                          )}
                        </div>
                        <div className="flex flex-col ml-3">
                          <div className="font-bold">{similarPart.material.name}</div>
                          <div>
                            {t(`PRODUCTION_METHODS.${similarPart.productionMethod}`, {
                              ns: 'bearing-hub/bearingHub',
                            })}
                          </div>
                          {typeof similarPart.unitPrice?.value !== 'undefined' &&
                            !Number.isNaN(similarPart.unitPrice.value) &&
                            similarPart.unitPrice.value > 0 && (
                              <div className="font-medium mt-5 text-lg">
                                {formatPrice(similarPart.unitPrice.value, locale, similarPart.unitPrice.unit)} /{' '}
                                {t(`HEADLINES.PIECE`, { ns: 'bearing-hub/bearingHub' })}
                                {typeof similarPart.setupCost?.value !== 'undefined' &&
                                  !Number.isNaN(similarPart.setupCost.value) &&
                                  similarPart.setupCost.value > 0 && (
                                    <p className="text-xxs mt-1 text-gray-300 whitespace-nowrap">
                                      {t(`PRICE_PLUS_SETUP_COSTS`, {
                                        price: formatPrice(
                                          similarPart.setupCost.value,
                                          locale,
                                          similarPart.setupCost.unit
                                        ),
                                        ns: 'bearing-hub/bearingHub',
                                      })}
                                    </p>
                                  )}
                              </div>
                            )}
                        </div>
                      </div>
                    )}
                    {showDeliveryTime && (
                      <div className="w-full flex flex-row mb-6">
                        <div className="w-[40%] pl-8 font-bold">
                          {t(`HEADLINES.DELIVERY_TIME`, { ns: 'bearing-hub/bearingHub' })}:
                        </div>
                        <div className="ml-4">
                          {similarPart.deliveryTimeInDays && !Number.isNaN(similarPart.deliveryTimeInDays) && (
                            // eslint-disable-next-line react/jsx-no-useless-fragment
                            <>
                              {similarPart.deliveryTimeInDays < 0 ? (
                                <span className="whitespace-nowrap">
                                  {t(`DELIVERY_TIMES.UPON_REQUEST`, { ns: 'bearing-hub/bearingHub' })}
                                </span>
                              ) : (
                                <span className="whitespace-nowrap">
                                  {similarPart.deliveryTimeInDays}{' '}
                                  {t(`HEADLINES.DAYS`, {
                                    count: similarPart.deliveryTimeInDays,
                                    ns: 'bearing-hub/bearingHub',
                                  })}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="w-full flex flex-col mb-6">
                      <div className="min-w-[40%] pl-8 font-bold mb-6">
                        {t(`HEADLINES.DIMENSIONS`, { ns: 'bearing-hub/bearingHub', unit: 'mm' })}:
                      </div>
                      <div className="ml-4 flex flex-wrap pr-8">
                        {similarPart?.dimensions ? (
                          <BearingTableDimensions
                            usedDimensions={dimensions}
                            selectedShape={selectedShape}
                            showHeader
                            data={similarPart}
                            locale={locale}
                            unitSystem={unitSystem}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="mt-5 px-6">
                      <Button
                        useInternalRouting
                        href={isLink ? linkTarget : undefined}
                        text={t(`COMMON.SIMILAR_PARTS.CHOOSE`, { ns: 'bearing-hub/bearingHub' })}
                        className="w-full"
                        onClick={() => {
                          const newDimensions: IbhDimensions = { ...similarPart.dimensions };
                          debouncedOnDimensionChange(newDimensions, similarPart);
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="hidden lg:block flex flex-wrap relative" data-testid="similar-parts-container-table">
              <table className="w-full border-none mt-6" data-testid="bearings-similar-table">
                <thead className="font-bold text-left text-sm" data-testid="bearings-similar-table-header">
                  <tr className="leading-[4rem] border-b-[1px] border-solid border-[#AFAFAF]">
                    {showArticleNumber && (
                      <th className="whitespace-nowrap">
                        {t(`HEADLINES.ARTICLE_NUMBER`, { ns: 'bearing-hub/bearingHub' })}
                      </th>
                    )}
                    {showDimensionsAsCols ? (
                      <>
                        {formDefinitions.map((formDefinition, idx) => (
                          <th
                            key={formDefinition}
                            className={idx === 0 ? 'pl-12' : ''}
                            data-testid="bearings-similar-cell-header-d1"
                          >
                            {formDefinition}{' '}
                            {formDefinition === 'slotAngle' ? (
                              <span className="font-normal">(°)</span>
                            ) : (
                              <span className="font-normal">({getLengthCode(unitSystem)})</span>
                            )}
                          </th>
                        ))}
                      </>
                    ) : (
                      <th className="whitespace-nowrap text-center pr-4">
                        <div className="w-full leading-normal">
                          {t(`HEADLINES.DIMENSIONS`, { ns: 'bearing-hub/bearingHub', unit: 'mm' })}
                        </div>
                        <div className="font-normal flex flex-row w-full leading-normal">
                          {ShapeDimensionsDefinitionForm[selectedShape.shapeKey].map(definition => {
                            let dimensionLabel = definition as string;

                            if (definition === dimensionKey.b2d3) {
                              dimensionLabel = 'b2.2';
                            }
                            if (definition === dimensionKey.b2d4) {
                              dimensionLabel = 'b2.1';
                            }
                            if (definition === 'slotAngle') {
                              dimensionLabel = 'T (°)';
                            }
                            if (definition === 'b1' && selectedShape.shapeKey === IbhShape.T) {
                              dimensionLabel = 's';
                            }

                            return (
                              <div key={definition} className="grow basis-0 text-center">
                                {dimensionLabel}
                              </div>
                            );
                          })}
                        </div>
                      </th>
                    )}
                    {showDeliveryTime && (
                      <th className="whitespace-nowrap pr-4">
                        {t(`HEADLINES.DELIVERY_TIME`, { ns: 'bearing-hub/bearingHub' })}
                      </th>
                    )}
                    {showPrice && (
                      <th className="whitespace-nowrap pr-8">
                        {`${t(`HEADLINES.PRICE`, { ns: 'bearing-hub/bearingHub' })} / ${t(`HEADLINES.PIECE`, {
                          ns: 'bearing-hub/bearingHub',
                        })}`}
                      </th>
                    )}
                    <th> </th>
                  </tr>
                </thead>
                <tbody className="text-left" data-testid="bearings-similar-table-body">
                  {similarParts.map(similarPart => {
                    const linkTarget = getRedirectLinkToProductDetails(
                      similarPart,
                      selectedShape,
                      unitSystem,
                      hashParams,
                      false,
                      locale
                    );

                    return (
                      <tr
                        key={similarPart.articleNumber}
                        className="leading-[4rem] border-b-[1px] border-solid border-[#AFAFAF]"
                        data-testid="similar-part-row"
                      >
                        {showArticleNumber && (
                          <td className="whitespace-nowrap text-sm pr-6">{similarPart.articleNumber}</td>
                        )}
                        {showDimensionsAsCols ? (
                          <>
                            {formDefinitions.map((formDefinition, idx) => {
                              const usedDimensionValue = dimensions[formDefinition as keyof IbhDimensions];
                              const newDimensionValue = similarPart.dimensions?.[formDefinition as keyof IbhDimensions];
                              return (
                                <td
                                  key={formDefinition}
                                  className={idx === 0 ? 'pl-12' : ''}
                                  data-testid={`bearings-similar-cell-${formDefinition}`}
                                  style={{ width: `${100 / formDefinitions.length}%` }}
                                >
                                  <span
                                    className={`${
                                      usedDimensionValue === newDimensionValue ? 'text-[#1C8B41]' : ''
                                    } text-sm font-normal`}
                                  >
                                    {typeof similarPart.dimensions?.[formDefinition as keyof IbhDimensions] !==
                                      'undefined' &&
                                      !Number.isNaN(similarPart.dimensions[formDefinition as keyof IbhDimensions]) &&
                                      createNumberFormatter(locale, {
                                        maximumFractionDigits: unitSystem === UnitSystem.imperial ? 4 : 2,
                                        minimumFractionDigits: unitSystem === UnitSystem.imperial ? 4 : 2,
                                      }).format(similarPart.dimensions[formDefinition as keyof IbhDimensions])}
                                  </span>
                                </td>
                              );
                            })}
                          </>
                        ) : (
                          <td className="pr-4 text-sm leading-normal">
                            <BearingTableDimensions
                              usedDimensions={dimensions}
                              selectedShape={selectedShape}
                              data={similarPart}
                              locale={locale}
                              unitSystem={unitSystem}
                            />
                          </td>
                        )}
                        {showDeliveryTime && (
                          <td className="w-[100px] pr-4 relative text-sm">
                            {similarPart.deliveryTimeInDays && !Number.isNaN(similarPart.deliveryTimeInDays) && (
                              <span className="z-5">
                                {similarPart.deliveryTimeInDays < 0 ? (
                                  <span className="whitespace-nowrap">
                                    {t(`DELIVERY_TIMES.UPON_REQUEST`, { ns: 'bearing-hub/bearingHub' })}
                                  </span>
                                ) : (
                                  <span className="whitespace-nowrap">
                                    {similarPart.deliveryTimeInDays}{' '}
                                    {t(`HEADLINES.DAYS`, {
                                      count: similarPart.deliveryTimeInDays,
                                      ns: 'bearing-hub/bearingHub',
                                    })}
                                  </span>
                                )}
                              </span>
                            )}
                          </td>
                        )}
                        {showPrice && (
                          <td className="w-[145px] pr-8 font-bold text-sm relative">
                            {typeof similarPart.unitPrice?.value !== 'undefined' &&
                              !Number.isNaN(similarPart.unitPrice.value) &&
                              similarPart.unitPrice.value > 0 && (
                                <span className="z-5">
                                  {formatPrice(similarPart.unitPrice.value, locale, similarPart.unitPrice.unit)}
                                  {typeof similarPart.setupCost?.value !== 'undefined' &&
                                    !Number.isNaN(similarPart.setupCost.value) &&
                                    similarPart.setupCost.value > 0 && (
                                      <p className="text-xxs mt-1 text-gray-300 whitespace-nowrap">
                                        {t(`PRICE_PLUS_SETUP_COSTS_SHORT`, {
                                          price: formatPrice(
                                            similarPart.setupCost.value,
                                            locale,
                                            similarPart.setupCost.unit
                                          ),
                                          ns: 'bearing-hub/bearingHub',
                                        })}
                                      </p>
                                    )}
                                </span>
                              )}
                          </td>
                        )}
                        <td className="w-[160px] min-w-[160px]" data-testid="bearings-similar-cell-choose">
                          <Link
                            className="uppercase font-bold justify-center"
                            text={t(`COMMON.SIMILAR_PARTS.CHOOSE`, { ns: 'bearing-hub/bearingHub' })}
                            primary
                            href={isLink ? linkTarget : undefined}
                            onClick={() => {
                              const newDimensions = { ...similarPart.dimensions };
                              onChoose?.(newDimensions, similarPart);
                            }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <span className="text-sm">
              <span className="text-[#1C8B41]">
                {t(`COMMON.SIMILAR_PARTS.GREEN`, { ns: 'bearing-hub/bearingHub' })}
              </span>{' '}
              = {t(`COMMON.SIMILAR_PARTS.HELP_TEXT`, { ns: 'bearing-hub/bearingHub' })}
            </span>
          </>
        </>
      )}
    </div>
  );
};
